import { TermLengthType } from '../types/types'

interface AccountCardProps {
   getString: (key: string, fallback?: string) => string
   accountName: string
   clientEmployeeName: string
   clientEmployeeEmail: string
   bhrEmployeeName: string
   bhrEmployeeEmail: string
   termLengthObject: TermLengthType | null
   activationDate: string
   employeeCount: number
   formType: number
   prepayLengthMonths: number
}

function formatSubscriptionRange(
   activationDate: string,
   termLengthMonths: number,
   prepayLengthMonths: number,
): string {
   if (!activationDate) return ''
   const start = new Date(activationDate)
   if (isNaN(start.getTime())) return ''
   const end = new Date(start)
   if (termLengthMonths > 1)
      end.setUTCMonth(end.getUTCMonth() + termLengthMonths)
   else if (prepayLengthMonths > 1)
      end.setUTCMonth(end.getUTCMonth() + prepayLengthMonths)
   else return ''
   const monthYear = (d: Date) =>
      new Intl.DateTimeFormat('en-US', {
         month: 'long',
         year: 'numeric',
         timeZone: 'UTC',
      }).format(d)
   return `${monthYear(start)} — ${monthYear(end)}`
}

function displayOrFallback(
   value: string | null | undefined,
   fallback: string,
): string {
   if (value === null || value === undefined) return fallback
   const trimmed = String(value).trim()
   if (trimmed === '' || trimmed.toLowerCase() === 'null') return fallback
   return trimmed
}

function formatSubscriptionTermName(pdf_name: string, term_length: number) {
   const baseName = (pdf_name || '')
      .replace(/\s*[-–—]\s*\d+\s*Months?\s*$/i, '')
      .replace(/^\s*\d+\s*Months?\s*[-–—]?\s*/i, '')
      .trim()
   if (!term_length || term_length <= 0) return baseName || pdf_name
   if (term_length === 1) return baseName || pdf_name
   return `${term_length} Months ${baseName}`.trim()
}

function formatLongDate(date: string): string {
   if (!date) return ''
   const d = new Date(date)
   if (isNaN(d.getTime())) return ''
   return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
   }).format(d)
}

export default function AccountCard({
   getString,
   accountName,
   clientEmployeeName,
   clientEmployeeEmail,
   bhrEmployeeName,
   bhrEmployeeEmail,
   termLengthObject,
   activationDate,
   employeeCount,
   formType,
   prepayLengthMonths,
}: AccountCardProps) {
   const headerLabel = getString('pdf.frontpage.account', 'Account')
   const accountNameLabel = getString(
      'pdf.frontpage.contactheader.account_name',
      'Account Name',
   )
   const preparedForLabel = getString(
      'pdf.frontpage.contactheader.prepared_for',
      'Prepared for:',
   )
   const preparedByLabel = getString(
      'pdf.frontpage.contactheader.prepared_by',
      'Prepared by:',
   )
   const subscriptionTermLabel = getString(
      'pdf.frontpage.subscriptionTerm',
      'Subscription Term:',
   )
   const activationDateLabel = getString(
      'pdf.frontpage.estimatedActivationDate',
      'Estimated Activation Date:',
   )
   const employeeCountLabel = getString(
      'pdf.frontpage.employeeCount',
      'Employee Count:',
   )

   return (
      <div className='accountCard'>
         <div className='accountCardHeader'>{headerLabel}</div>
         <div className='accountCardBody'>
            <div className='accountField'>
               <div className='accountFieldLabel'>{accountNameLabel}</div>
               <div className='accountFieldValue'>
                  {displayOrFallback(accountName, 'No Account Name On File')}
               </div>
            </div>
            <div className='accountField'>
               <div className='accountFieldLabel'>{preparedForLabel}</div>
               <div className='accountFieldValue'>
                  {displayOrFallback(
                     clientEmployeeName,
                     'No contact name on file',
                  )}
               </div>
               <div className='accountFieldValue'>
                  {displayOrFallback(
                     clientEmployeeEmail,
                     'No contact email on file',
                  )}
               </div>
            </div>
            <div className='accountField'>
               <div className='accountFieldLabel'>{preparedByLabel}</div>
               <div className='accountFieldValue'>
                  {displayOrFallback(bhrEmployeeName, 'No Name On File')}
               </div>
               <div className='accountFieldValue bambooLeaf'>
                  {displayOrFallback(bhrEmployeeEmail, 'No Email On File')}
               </div>
            </div>
            {termLengthObject && (
               <div className='accountField'>
                  <div className='accountFieldLabel'>
                     {subscriptionTermLabel}
                  </div>
                  <div className='accountFieldValue'>
                     {formatSubscriptionTermName(
                        termLengthObject.pdf_name,
                        termLengthObject.term_length,
                     )}
                  </div>
                  {activationDate && termLengthObject.term_length > 0 && (
                     <div className='accountFieldValue'>
                        {formatSubscriptionRange(
                           activationDate,
                           termLengthObject.term_length,
                           prepayLengthMonths,
                        )}
                     </div>
                  )}
               </div>
            )}
            {activationDate && formType === 4 && (
               <div className='accountField'>
                  <div className='accountFieldLabel'>{activationDateLabel}</div>
                  <div className='accountFieldValue'>
                     {formatLongDate(activationDate)}
                  </div>
               </div>
            )}
            <div className='accountField'>
               <div className='accountFieldLabel'>{employeeCountLabel}</div>
               <div className='accountFieldValue'>{employeeCount}</div>
            </div>
         </div>
      </div>
   )
}
